import buildSubjectApiPath from '@Application/Subjects/buildSubjectApiPath';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import * as React from 'react';
import type TranscriptTurnsListView from '@UI/Store/Interviews/Views/TranscriptTurnsListView';
import AudioPlayerContext from './AudioPlayerContext';
import useStrictCurrentFacility from '@UI/Providers/CurrentFacilityProvider/useStrictCurrentFacility';
import type TranscriptTurnView from '@UI/Store/Interviews/Views/TranscriptTurnView';

type Props = {
	children: React.ReactNode;
	readonly subject: SubjectAddress;
	readonly interviewId: number;
	readonly recordingStatus: 'stored' | 'uploading' | null;
	readonly recordingId: number | null;
	readonly turns: TranscriptTurnsListView | null;
	readonly isLoadingNextPage: boolean;
	readonly loadNextPage: () => void;
};

const PLAYBACK_RATES: readonly number[] = Object.freeze([1, 1.25, 1.5, 2]);

const AudioPlayerProvider: React.FC<Props> = ({
	children,
	turns,
	isLoadingNextPage,
	loadNextPage,
	recordingId,
	subject,
	interviewId,
	recordingStatus,
}) => {
	const { currentFacility } = useStrictCurrentFacility();
	const facilityId = currentFacility.id;

	const audioRef = React.useRef<HTMLAudioElement | null>(null);
	const hasAutoRequestedForGapRef = React.useRef(false);

	const [isPlaying, setIsPlaying] = React.useState(false);
	const [currentTime, setCurrentTime] = React.useState(0);
	const [duration, setDuration] = React.useState(0);
	const [isPositionable, setIsPositionable] = React.useState(false);
	const [playbackRate, setPlaybackRate] = React.useState(PLAYBACK_RATES[0] ?? 1);
	const [activeTurnId, setActiveTurnId] = React.useState<number | null>(null);
	const [hasPlaybackExceededLoadedTurns, setHasPlaybackExceededLoadedTurns] = React.useState(false);

	const isAvailable = recordingStatus === 'stored' && recordingId !== null;

	const audioSrc = isAvailable
		? `${buildSubjectApiPath(facilityId, subject)}/interviews/${interviewId}/recordings/${recordingId}/audio`
		: null;

	React.useEffect(() => {
		const audio = audioRef.current;

		if (audio === null || audioSrc === null) {
			return undefined;
		}

		const handlePlay = (): void => setIsPlaying(true);
		const handlePause = (): void => setIsPlaying(false);
		const handleLoadedMetadata = (): void => {
			setDuration(Number.isFinite(audio.duration) ? audio.duration : 0);
			setIsPositionable(audio.seekable.length > 0);
		};
		const handleProgress = (): void => setIsPositionable(audio.seekable.length > 0);

		const handleTimeUpdate = (): void => {
			const time = audio.currentTime;
			setCurrentTime(time);

			const loadedTurns = turns?.list ?? [];
			const coveringTurn = loadedTurns.find(
				(turn) =>
					turn.startSecond !== null &&
					turn.endSecond !== null &&
					turn.startSecond <= time &&
					time < turn.endSecond,
			);

			if (coveringTurn !== undefined) {
				setActiveTurnId(coveringTurn.id);
				setHasPlaybackExceededLoadedTurns(false);
				hasAutoRequestedForGapRef.current = false;
				return;
			}

			setActiveTurnId(null);

			const lastLoadedTurn = loadedTurns.length > 0 ? loadedTurns[loadedTurns.length - 1] : undefined;
			const lastLoadedEnd = lastLoadedTurn?.endSecond ?? lastLoadedTurn?.startSecond ?? null;
			const hasReachedLoadedEnd = lastLoadedEnd !== null && time >= lastLoadedEnd;
			const hasMore = turns?.hasMore ?? false;

			if (!hasReachedLoadedEnd || !hasMore) {
				setHasPlaybackExceededLoadedTurns(false);
				return;
			}

			setHasPlaybackExceededLoadedTurns(true);

			if (!isLoadingNextPage && !hasAutoRequestedForGapRef.current) {
				hasAutoRequestedForGapRef.current = true;
				loadNextPage();
			}
		};

		audio.addEventListener('play', handlePlay);
		audio.addEventListener('pause', handlePause);
		audio.addEventListener('loadedmetadata', handleLoadedMetadata);
		audio.addEventListener('progress', handleProgress);
		audio.addEventListener('timeupdate', handleTimeUpdate);

		return () => {
			audio.removeEventListener('play', handlePlay);
			audio.removeEventListener('pause', handlePause);
			audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
			audio.removeEventListener('progress', handleProgress);
			audio.removeEventListener('timeupdate', handleTimeUpdate);
		};
	}, [audioSrc, turns, isLoadingNextPage, loadNextPage]);

	const togglePlay = React.useCallback((): void => {
		const audio = audioRef.current;

		if (audio === null) {
			return;
		}

		if (audio.paused) {
			audio.play().catch(() => {
				// A rejected `play()` (autoplay policy, decode failure) simply leaves playback paused;
				// `isPlaying` already reflects the real media state through the `pause` event.
			});
			return;
		}

		audio.pause();
	}, []);

	// A container without a seek table reports an empty `seekable` regardless of the requested time.
	const seekTo = React.useCallback((seconds: number): void => {
		const audio = audioRef.current;

		if (audio === null || audio.seekable.length === 0) {
			return;
		}

		const upperBound = Number.isFinite(audio.duration) ? audio.duration : seconds;
		const target = Math.min(Math.max(0, seconds), upperBound);

		audio.currentTime = target;
		setCurrentTime(target);
	}, []);

	const skipBy = React.useCallback(
		(deltaSeconds: number): void => {
			const audio = audioRef.current;

			if (audio !== null) {
				seekTo(audio.currentTime + deltaSeconds);
			}
		},
		[seekTo],
	);

	// An engine without timestamps carries `startSecond === null`: there is nothing to seek to.
	const seekToTurn = React.useCallback(
		(turn: TranscriptTurnView): void => {
			if (turn.startSecond !== null) {
				seekTo(turn.startSecond);
			}
		},
		[seekTo],
	);

	// `defaultPlaybackRate` is written too: reloading the source resets `playbackRate` to it.
	const cyclePlaybackRate = React.useCallback((): void => {
		const audio = audioRef.current;
		const nextIndex = (PLAYBACK_RATES.indexOf(playbackRate) + 1) % PLAYBACK_RATES.length;
		const nextRate = PLAYBACK_RATES[nextIndex] ?? 1;

		if (audio !== null) {
			audio.playbackRate = nextRate;
			audio.defaultPlaybackRate = nextRate;
		}

		setPlaybackRate(nextRate);
	}, [playbackRate]);

	const contextValue = React.useMemo(
		() => ({
			audioRef,
			audioSrc,
			isAvailable,
			isPlaying,
			currentTime,
			duration,
			isPositionable,
			activeTurnId,
			hasPlaybackExceededLoadedTurns,
			playbackRate,
			togglePlay,
			seekToTurn,
			seekTo,
			skipBy,
			cyclePlaybackRate,
		}),
		[
			audioSrc,
			isAvailable,
			isPlaying,
			currentTime,
			duration,
			isPositionable,
			activeTurnId,
			hasPlaybackExceededLoadedTurns,
			playbackRate,
			togglePlay,
			seekToTurn,
			seekTo,
			skipBy,
			cyclePlaybackRate,
		],
	);

	return <AudioPlayerContext.Provider value={contextValue}>{children}</AudioPlayerContext.Provider>;
};

export default AudioPlayerProvider;
